import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Share2, ExternalLink, Lock, ShieldAlert } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../components/ui/sonner";
import { MessageThread } from "../components/MessageThread";
import { FeePaymentPanel } from "../components/FeePaymentPanel";
import { ReportConcernModal } from "../components/ReportConcernModal";
import { IMG_MATCH_BG } from "../lib/images";
import { formatDate, formatTime } from "../lib/dateUtils";

const EMPTY_REVIEW = {
  photographer_rating: 5, photographer_comment: "",
  experience_rating: 5, experience_comment: "",
  would_participate_again: null, would_recommend: null,
  private_feedback: "", match_feedback: "",
};

export default function BookingDetail() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [reviewSent, setReviewSent] = useState(false);
  const [review, setReview] = useState(EMPTY_REVIEW);
  const [showReportModal, setShowReportModal] = useState(false);

  const load = () => {
    api
      .get(`/bookings/${id}`)
      .then(({ data }) => setBooking(data))
      .catch(() => setBooking(false));
    api.get(`/bookings/${id}/gallery`).then(({ data }) => setGallery(data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (booking && booking.status === "completed") {
      api
        .get(`/reviews/mine/${booking.session_id}`)
        .then(() => setReviewSent(true))
        .catch(() => {});
    }
  }, [booking]);

  const cancelBooking = async () => {
    await api.delete(`/bookings/${id}`);
    toast.success("Booking cancelled");
    load();
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { session_id: booking.session_id, ...review });
      setReviewSent(true);
      toast.success("Thanks for sharing your story!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (booking === null) return <div className="py-32 text-center font-medium">Loading...</div>;
  if (booking === false) return <div className="py-32 text-center font-medium">Booking not found.</div>;

  const session = booking.session;

  if (booking.status === "cancelled") {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="booking-cancelled-state">
        <h1 className="font-extrabold uppercase text-2xl mb-3">Booking Cancelled</h1>
        <p className="text-midnight-black/60 font-medium mb-6">You're no longer registered for {session?.title}.</p>
        <Link to="/find-a-session">
          <Button className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">FIND ANOTHER SESSION</Button>
        </Link>
      </div>
    );
  }

  if (booking.status === "registered" || booking.status === "waitlisted") {
    const isWaitlisted = booking.status === "waitlisted";
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid={isWaitlisted ? "booking-waitlisted-state" : "booking-waiting-state"}>
        <p className="text-stranger-orange text-xs font-bold tracking-[0.25em] uppercase mb-4">{isWaitlisted ? "Waitlisted" : "Registered"}</p>
        <h1 className="font-extrabold uppercase text-2xl sm:text-3xl mb-3">{isWaitlisted ? "You're On the Waitlist" : "You're In. Now We Wait."}</h1>
        <p className="text-midnight-black/70 font-medium mb-8">
          {isWaitlisted
            ? `${session?.title} on ${formatDate(session?.session_date)} was full when you registered. We'll email you if a spot opens up.`
            : `You're registered for ${session?.title} on ${formatDate(session?.session_date)}. We'll match you with your Stranger soon — check back here.`}
        </p>
        <Button data-testid="cancel-booking-button" variant="outline" onClick={cancelBooking} className="rounded-full font-bold">
          {isWaitlisted ? "LEAVE WAITLIST" : "CANCEL REGISTRATION"}
        </Button>
        {!isWaitlisted && <FeePaymentPanel bookingId={id} partyType="stranger" bookingNumber={booking.booking_number} wrapperClassName="mt-8 text-left" />}
        <div className="mt-10 text-left">
          <MessageThread bookingId={id} />
        </div>
        <button data-testid="report-concern-button" onClick={() => setShowReportModal(true)} className="mt-8 flex items-center gap-1.5 mx-auto text-[11px] font-bold tracking-widest text-midnight-black/40 hover:text-red-500">
          <ShieldAlert className="w-3.5 h-3.5" /> REPORT A CONCERN
        </button>
        {showReportModal && <ReportConcernModal bookingId={id} onClose={() => setShowReportModal(false)} />}
      </div>
    );
  }

  if (booking.status === "matched") {
    return (
      <div className="relative min-h-[70vh] flex items-center justify-center text-center px-5" data-testid="booking-matched-state">
        <div className="absolute inset-0">
          <img src={IMG_MATCH_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-midnight-black/80" />
        </div>
        <div className="relative z-10 max-w-lg">
          <p className="text-stranger-orange font-bold tracking-[0.3em] uppercase text-xs mb-6">You've Been Matched</p>
          <h1 className="text-clean-white font-extrabold uppercase text-3xl sm:text-4xl mb-6">Your Stranger is waiting.</h1>
          <p className="text-clean-white font-bold text-xl mb-1">{formatDate(session?.session_date)} · {formatTime(session?.session_time)}</p>
          <p className="text-clean-white/80 font-medium mb-8">
            {session?.address}, {session?.city}, {session?.state}
          </p>

          <div className="grid grid-cols-2 gap-6 text-left bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
            <div>
              <p className="text-stranger-orange text-xs font-bold tracking-widest uppercase mb-2">What to Wear</p>
              <p className="text-clean-white/90 text-sm font-medium">{session?.what_to_wear || "Casual, earth tones."}</p>
            </div>
            <div>
              <p className="text-stranger-orange text-xs font-bold tracking-widest uppercase mb-2">What to Bring</p>
              <p className="text-clean-white/90 text-sm font-medium">Just yourself.</p>
            </div>
          </div>

          <p className="text-clean-white/60 italic font-medium text-sm mb-8" data-testid="match-mystery-line">
            Don't worry. They don't know who you are either.
          </p>
          <Button data-testid="cancel-booking-button" variant="outline" onClick={cancelBooking} className="rounded-full font-bold border-white/30 text-clean-white bg-transparent hover:bg-white/10">
            CANCEL REGISTRATION
          </Button>
          <FeePaymentPanel bookingId={id} partyType="stranger" bookingNumber={booking.booking_number} wrapperClassName="mt-8 bg-clean-white text-left" />
          <div className="mt-6 bg-clean-white rounded-2xl p-6 text-left">
            <MessageThread bookingId={id} />
          </div>
          <button data-testid="report-concern-button" onClick={() => setShowReportModal(true)} className="mt-8 flex items-center gap-1.5 mx-auto text-[11px] font-bold tracking-widest text-clean-white/50 hover:text-red-400">
            <ShieldAlert className="w-3.5 h-3.5" /> REPORT A CONCERN
          </button>
          {showReportModal && <ReportConcernModal bookingId={id} onClose={() => setShowReportModal(false)} />}
        </div>
      </div>
    );
  }

  if (!session?.gallery_ready) {
    const expired = gallery?.gallery_status === "expired";
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="booking-photos-pending-state">
        {expired ? (
          <>
            <h1 className="font-extrabold uppercase text-2xl sm:text-3xl mb-3" data-testid="gallery-expired-message">Your Gallery Link Has Expired</h1>
            <p className="text-midnight-black/70 font-medium">Reach out to your photographer for a new link — your photos are still safe with them.</p>
          </>
        ) : (
          <>
            <h1 className="font-extrabold uppercase text-2xl sm:text-3xl mb-3">Your Session Happened. Your Photos Are Next.</h1>
            <p className="text-midnight-black/70 font-medium">Your photographer is putting together your gallery. We'll let you know the moment it's ready.</p>
          </>
        )}
        <button data-testid="report-concern-button" onClick={() => setShowReportModal(true)} className="mt-10 flex items-center gap-1.5 mx-auto text-[11px] font-bold tracking-widest text-midnight-black/40 hover:text-red-500">
          <ShieldAlert className="w-3.5 h-3.5" /> REPORT A CONCERN
        </button>
        {showReportModal && <ReportConcernModal bookingId={id} onClose={() => setShowReportModal(false)} />}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16" data-testid="booking-story-ready-state">
      <p className="text-stranger-orange text-xs font-bold tracking-[0.25em] uppercase mb-3">Story Ready</p>
      <h1 className="font-extrabold uppercase text-3xl sm:text-4xl tracking-tight mb-8">Your Story Is Ready.</h1>

      <div className="rounded-2xl overflow-hidden mb-8">
        <img src={session.image_url} alt={session.title} className="w-full h-72 object-cover" />
      </div>

      {session.gallery_photos?.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-8">
          {session.gallery_photos.map((p) => (
            <div key={p.path} className="aspect-square bg-soft-gray rounded-lg flex items-center justify-center text-xs font-medium text-midnight-black/50 p-2 text-center">
              {p.filename}
            </div>
          ))}
        </div>
      )}

      {session.gallery_url && (
        <div className="mb-10">
          <a href={session.gallery_url} target="_blank" rel="noreferrer" data-testid="external-gallery-link">
            <Button className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">
              <ExternalLink className="w-4 h-4 mr-2" /> VIEW YOUR PHOTOS
            </Button>
          </a>
          {gallery?.gallery_password && (
            <p className="mt-3 text-sm font-medium text-midnight-black/60 flex items-center gap-1.5" data-testid="gallery-password-display">
              <Lock className="w-3.5 h-3.5" /> Gallery password: <strong>{gallery.gallery_password}</strong>
            </p>
          )}
          {gallery?.gallery_expiration_date && (
            <p className="mt-1 text-xs font-medium text-midnight-black/40">Link available until {formatDate(gallery.gallery_expiration_date)}.</p>
          )}
        </div>
      )}

      <div className="flex gap-3 mb-10">
        <Button
          variant="outline"
          data-testid="share-session-button"
          onClick={() => {
            navigator.clipboard?.writeText(window.location.href);
            toast.success("Link copied");
          }}
          className="rounded-full font-bold"
        >
          <Share2 className="w-4 h-4 mr-2" /> SHARE
        </Button>
        <Link to="/find-a-session">
          <Button variant="outline" data-testid="book-another-session-button" className="rounded-full font-bold">
            BOOK ANOTHER SESSION
          </Button>
        </Link>
      </div>

      <div className="border border-soft-gray rounded-2xl p-6 mb-6">
        <h2 className="font-extrabold uppercase text-lg mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-stranger-orange" /> Leave a Review
        </h2>
        {reviewSent ? (
          <p className="text-sm font-medium text-midnight-black/70" data-testid="review-sent-message">
            Thanks for sharing your story!
          </p>
        ) : (
          <div className="space-y-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/50 mb-2">Rate Your Photographer</p>
              <div className="flex gap-1 mb-2" data-testid="photographer-rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`photographer-star-${n}`} onClick={() => setReview({ ...review, photographer_rating: n })}>
                    <Star className={`w-6 h-6 ${n <= review.photographer_rating ? "text-stranger-orange fill-stranger-orange" : "text-soft-gray"}`} />
                  </button>
                ))}
              </div>
              <Textarea data-testid="photographer-comment-input" placeholder="How was your photographer?" value={review.photographer_comment} onChange={(e) => setReview({ ...review, photographer_comment: e.target.value })} />
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/50 mb-2">Rate Your Overall Experience</p>
              <div className="flex gap-1 mb-2" data-testid="experience-rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`experience-star-${n}`} onClick={() => setReview({ ...review, experience_rating: n })}>
                    <Star className={`w-6 h-6 ${n <= review.experience_rating ? "text-stranger-orange fill-stranger-orange" : "text-soft-gray"}`} />
                  </button>
                ))}
              </div>
              <Textarea data-testid="experience-comment-input" placeholder="Tell us about your Stranger Session — this may be shared on our public Stories page." value={review.experience_comment} onChange={(e) => setReview({ ...review, experience_comment: e.target.value })} />
            </div>

            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/50 mb-2">Would you participate again?</p>
                <div className="flex gap-2">
                  <button type="button" data-testid="participate-again-yes" onClick={() => setReview({ ...review, would_participate_again: true })} className={`px-4 py-1.5 rounded-full text-xs font-bold border-2 ${review.would_participate_again === true ? "bg-midnight-black text-clean-white border-midnight-black" : "border-soft-gray"}`}>YES</button>
                  <button type="button" data-testid="participate-again-no" onClick={() => setReview({ ...review, would_participate_again: false })} className={`px-4 py-1.5 rounded-full text-xs font-bold border-2 ${review.would_participate_again === false ? "bg-midnight-black text-clean-white border-midnight-black" : "border-soft-gray"}`}>NO</button>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/50 mb-2">Would you recommend us?</p>
                <div className="flex gap-2">
                  <button type="button" data-testid="recommend-yes" onClick={() => setReview({ ...review, would_recommend: true })} className={`px-4 py-1.5 rounded-full text-xs font-bold border-2 ${review.would_recommend === true ? "bg-midnight-black text-clean-white border-midnight-black" : "border-soft-gray"}`}>YES</button>
                  <button type="button" data-testid="recommend-no" onClick={() => setReview({ ...review, would_recommend: false })} className={`px-4 py-1.5 rounded-full text-xs font-bold border-2 ${review.would_recommend === false ? "bg-midnight-black text-clean-white border-midnight-black" : "border-soft-gray"}`}>NO</button>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Match Experience Feedback <span className="text-midnight-black/40">(optional, for our matching team only)</span></p>
              <Textarea data-testid="match-feedback-input" placeholder="How did the pairing feel? This helps us match better." value={review.match_feedback} onChange={(e) => setReview({ ...review, match_feedback: e.target.value })} />
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Private Feedback <span className="text-midnight-black/40">(optional, never shown publicly)</span></p>
              <Textarea data-testid="private-feedback-input" placeholder="Anything you'd rather tell us privately?" value={review.private_feedback} onChange={(e) => setReview({ ...review, private_feedback: e.target.value })} />
            </div>

            <Button data-testid="submit-review-button" onClick={submitReview} className="bg-midnight-black text-clean-white font-bold rounded-full">
              SUBMIT REVIEW
            </Button>
          </div>
        )}
      </div>

      <button data-testid="report-concern-button" onClick={() => setShowReportModal(true)} className="flex items-center gap-1.5 mx-auto text-[11px] font-bold tracking-widest text-midnight-black/40 hover:text-red-500">
        <ShieldAlert className="w-3.5 h-3.5" /> REPORT A CONCERN
      </button>
      {showReportModal && <ReportConcernModal bookingId={id} onClose={() => setShowReportModal(false)} />}
    </div>
  );
}
